import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";

// ----------------------------------------------------
//     COACHED WARMUP → PHASE → EXERCISE PAGE
// ----------------------------------------------------

export const AthleteCoachedWarmupRoutinePhaseExercises = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // DATA PASSED FROM PREVIOUS PAGE
  const phase = location.state?.phase;
  const templateTitle = location.state?.template_title || "Warmup Routine";
  const phases = location.state?.phases || []; // <--- important for back navigation

  const exercises = phase?.exercises || [];

  console.log(JSON.stringify(phase.exercises, null, 2));

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState("");

  // ---------------- BACK -----------------
  const handleBackClick = () => {
    navigate("/athlete/coached/warmup-routine-phases", {
      state: {
        phases, // pass previously loaded phases
        template_title: templateTitle,
      },
    });
  };

  const openVideoModal = (videoUrl) => {
    setSelectedVideo(videoUrl);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedVideo("");
    setModalOpen(false);
  };

  // ----------- YOUTUBE HELPERS ------------
  const getYouTubeId = (url) => {
    if (!url) return null;

    try {
      const u = new URL(url);

      if (u.pathname.startsWith("/shorts/")) {
        return u.pathname.replace("/shorts/", "").split("?")[0];
      }
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      if (u.hostname === "youtu.be") return u.pathname.slice(1);

      return null;
    } catch {
      return null;
    }
  };

  const getEmbedUrl = (url) => {
    const id = getYouTubeId(url);
    if (!id) return null;

    const params = "rel=0&playsinline=1&autoplay=1";

    // Shorts
    if (url.includes("/shorts/"))
      return `https://www.youtube.com/embed/${id}?${params}`;

    // Normal video
    return `https://www.youtube.com/embed/${id}?${params}`;
  };

  const getThumbnail = (url) => {
    const id = getYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
  };

  return (
    <AthleteLayout>
      <div className="bg-white h-full flex flex-col">
        <div className="flex-1 overflow-y-auto">
          {/* HEADER */}
          <div className="bg-white">
            <AthleteHeader />
          </div>

          {/* TEMPLATE TITLE */}
          <div className="p2-3 w-full max-w-2xl mx-auto flex flex-row justify-center items-center gap-10 px-5">
            <p className="w-full py-3 text-black text-xl line-clamp-2 leading-tight text-center border-b border-black/30">
              {templateTitle}
            </p>
          </div>

          {/* MAIN CONTAINER */}
          <div className="w-full max-w-2xl mx-auto px-5">
            <div className="w-full py-5  relative">
              {/* PHASE HEADER */}
              <div className="rounded-xl bg-black flex items-center gap-3 p-3 mb-5">
                <p className="relative">
                  <span>
                    <ArrowLWhite onClick={handleBackClick} />
                  </span>
                </p>

                <p className="text-white text-base capitalize">
                  {phase?.title}
                </p>
              </div>

              {/* REST PHASE */}
              {phase?.type === "RestPhase" && (
                <div className="px-5 mt-4 mb-4">
                  <div className="rounded-xl p-4 border border-black/90">
                    <p className="text-black text-lg mb-2">Rest Phase</p>

                    <p className="text-black text-sm font-medium">
                      <strong>Duration:</strong> {phase.rest_duration || "—"}
                    </p>

                    <p className="text-black text-sm font-medium mt-1">
                      <strong>Notes:</strong> {phase.rest_notes || "—"}
                    </p>
                  </div>
                </div>
              )}

              <div className="mb-5">
                <div className="bg-black rounded-xl py-3 px-3 xs:pr-8 divide-x-2 divide-white/50 flex flex-row justify-between items-stretch text-center relative z-10">
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Sets
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Reps
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase hidden xs:block">
                      Load/Time
                    </p>
                    <p className="text-[#fcfbfb] text-[9px] xs:text-xs font-light uppercase block xs:hidden">
                      Load/Time
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Distance
                    </p>
                  </div>
                  <div className="flex-1 px-2">
                    <p className="text-[#fcfbfb] text-[10px] xs:text-xs font-light uppercase">
                      Rest
                    </p>
                  </div>
                </div>
              </div>

              {/* EXERCISE LIST */}
              <div className="h-full overflow-hidden overflow-y-auto xs:px-5">
                {exercises.length > 0 ? (
                  exercises.map((exercise, index) => {
                    const embedUrl = getEmbedUrl(exercise.video_url);
                    const thumb = getThumbnail(exercise.video_url);

                    return (
                      <div
                        key={index}
                        className="flex flex-col space-y-2 py-4 border-b-2 border-darkYellow cursor-pointer"
                        onClick={() => embedUrl && openVideoModal(embedUrl)}
                      >
                        <div className="flex items-start gap-3 xs:gap-5">
                          <div className="w-10 h-10 xs:w-14 xs:h-14 shrink-0">
                            {thumb && (
                              <img
                                src={thumb}
                                alt="video"
                                className="w-10 h-10 xs:w-14 xs:h-14 object-cover"
                              />
                            )}
                          </div>
                          <div>
                            <p className="text-black text-start text-base xs:text-lg font-bold capitalize">
                              {exercise.title}
                            </p>
                            <div className="flex flex-row flex-wrap items-stretch cursor-pointer font-medium text-xs xs:text-base text-grayish">
                              {/* sets */}
                              {exercise.sets ? (
                                <p>
                                  <span className="whitespace-nowrap">
                                    {exercise.sets} Sets x&nbsp;
                                  </span>
                                </p>
                              ) : (
                                <p>
                                  <span className="whitespace-nowrap">
                                    0 Sets x&nbsp;
                                  </span>
                                </p>
                              )}
                              {/* reps */}
                              {exercise.reps ? (
                                <p>
                                  <span className="whitespace-nowrap">
                                    {exercise.reps} Reps, &nbsp;
                                  </span>
                                </p>
                              ) : (
                                <p>
                                  <span className="whitespace-nowrap">
                                    0 Reps, &nbsp;
                                  </span>
                                </p>
                              )}
                              {/* time */}
                              {exercise.time && exercise.time != "None" ? (
                                <p>
                                  <span className="whitespace-nowrap">
                                    {exercise.time} Time, &nbsp;
                                  </span>
                                </p>
                              ) : (
                                <p>
                                  <span className="whitespace-nowrap">
                                    0 Time, &nbsp;
                                  </span>
                                </p>
                              )}
                              {/* distance */}
                              {exercise.distance ? (
                                <p>
                                  <span className="whitespace-nowrap">
                                    {exercise.distance} Distance, &nbsp;
                                  </span>
                                </p>
                              ) : (
                                <p>
                                  <span className="whitespace-nowrap">
                                    0 Distance, &nbsp;
                                  </span>
                                </p>
                              )}
                              {/* rest */}
                              {exercise.rest && (
                                <p className="whitespace-nowrap">
                                  <span className="italic">
                                    {exercise.rest} &nbsp;
                                  </span>
                                  <span>Rest,</span>
                                </p>
                              )}
                            </div>
                            {/* Notes (if any) */}
                            {exercise.notes && (
                              <div className="mt-1">
                                <span className="text-black/80 font-medium text-xs xs:text-sm">
                                  Notes:
                                </span>
                                <p className="text-grayish font-medium text-xs xs:text-sm ">
                                  {exercise.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-black text-center mt-5">
                    No exercises found.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* VIDEO MODAL */}
          {modalOpen && (
            <div
              className="fixed inset-0 flex justify-center items-center bg-black/80 p-3 z-50"
              onClick={closeModal}
            >
              <div
                className="relative bg-white w-full max-w-4xl rounded-lg p-4 xs:p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-2 right-2 text-white bg-black p-3 rounded-full size-10 text-lg leading-none z-[9999]"
                  onClick={closeModal}
                >
                  ✕
                </button>

                <div
                  className="relative w-full h-0 z-[1]"
                  style={{ paddingBottom: "56.25%" }}
                >
                  <iframe
                    src={`${selectedVideo}?autoplay=1`}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

// BACK ARROW
const ArrowLWhite = ({ onClick }) => (
  <svg
    onClick={onClick}
    className="hover:opacity-50 rotate-180 cursor-pointer"
    xmlns="http://www.w3.org/2000/svg"
    width="8"
    height="14"
    viewBox="0 0 8 14"
    fill="none"
  >
    <path
      d="M1 13L7 7L1 1"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
